import { Dispatch, FC, KeyboardEvent, SetStateAction, useEffect, useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { useLazyLoadQuery, UseQueryLoaderLoadQueryOptions } from 'react-relay';
import { allSites, getClassesForAssignmentDropdown } from '../../graphQLOperations/Classes';
import { ClassesAllSitesQuery } from '../../graphQLOperations/__generated__/ClassesAllSitesQuery.graphql';
import { Dropdown } from 'primereact/dropdown';
import { Site } from '../../models/Site';
import { Roles } from '../../models/User';
import { selectUser } from '../../redux/reducers/userSlice';
import { useAppSelector } from '../../redux/hooks';
import {
  ClassesAssignmentDropdownQuery,
  ClassesAssignmentDropdownQuery$data,
} from '../../graphQLOperations/__generated__/ClassesAssignmentDropdownQuery.graphql';
import { AssignmentStudentsWithSkillStatesQuery$variables } from '../../graphQLOperations/__generated__/AssignmentStudentsWithSkillStatesQuery.graphql';
import { SelectedSkillForAssignment } from '../../models/Assignment';
import { formatClassDisplayName } from '../../utils/formatClassDisplayName';
import { MultiSelect } from 'primereact/multiselect';
import { sortClassesList } from '../../utils/sortClassesList';
import { Class } from '../../models/Class';
import { useHiddenClassIds } from '../../custom-hooks/useHiddenClassIds';

interface Props {
  selectedSkillForAssignment: SelectedSkillForAssignment;
  loadQuery: (
    variables: AssignmentStudentsWithSkillStatesQuery$variables,
    options: UseQueryLoaderLoadQueryOptions,
  ) => void;
  selectedClasses: Class[];
  setSelectedClasses: Dispatch<SetStateAction<Class[]>>;
  previewEnabled: boolean;
  setAssignmentName: Dispatch<SetStateAction<string>>;
  assignmentName: string;
  invalidName: boolean;
  setInvalidName: Dispatch<SetStateAction<boolean>>;
}

export const AssignmentSidebarHeader: FC<Props> = ({
  selectedSkillForAssignment,
  loadQuery,
  selectedClasses,
  setSelectedClasses,
  previewEnabled,
  setAssignmentName,
  assignmentName,
  invalidName,
  setInvalidName,
}) => {
  const classesData = useLazyLoadQuery<ClassesAssignmentDropdownQuery>(
    getClassesForAssignmentDropdown,
    {},
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (selectedClasses.length) {
      loadQuery(
        { skillId: selectedSkillForAssignment.id, classIds: selectedClasses.map(c => c.id) },
        { fetchPolicy: 'network-only' },
      );
    }
  }, [selectedClasses]);

  const hiddenClassIds = useHiddenClassIds();
  const [allClassesData, setAllClassesData] = useState<Class[]>([]);
  useEffect(() => {
    const typedClassesData = classesData as ClassesAssignmentDropdownQuery$data;
    setAllClassesData(
      (typedClassesData?.classes?.nodes as Class[])
        .filter(c => !hiddenClassIds.has(c.id))
        .map(c => ({
          ...c,
          sectionName: formatClassDisplayName(c),
        })),
    );
  }, [classesData, hiddenClassIds]);

  const user = useAppSelector(selectUser);
  const adminRoles = [Roles.NETWORK_ADMIN, Roles.SITE_ADMIN];
  const isAdmin = adminRoles.some(role => user?.roles?.some(userRole => userRole?.name === role));
  const sitesData = useLazyLoadQuery<ClassesAllSitesQuery>(
    allSites,
    { input: { userId: `UserNode:${user?.id ?? ''}` } },
    { fetchPolicy: 'network-only' },
  );
  const sites: Site[] = useMemo(
    () =>
      ((sitesData?.sites?.nodes?.filter(Boolean) as Site[] | undefined) ?? []).sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [sitesData?.sites?.nodes],
  );

  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  useEffect(() => {
    if (!selectedSite && sites.length === 1) {
      setSelectedSite(sites[0]);
    }
  }, [sites, selectedSite]);

  const showSiteDropdown = isAdmin && sites.length > 0;
  const classesForSite = selectedSite
    ? allClassesData.filter(c => c.site?.id === selectedSite.id)
    : showSiteDropdown
    ? []
    : allClassesData;

  const nameField = (
    <div className={`field ${showSiteDropdown ? 'col-12 pb-0 mb-1' : 'ml-1 col-6 mx-1'}`}>
      <label htmlFor="assignment-name" className={`${invalidName ? 'p-error' : ''}`}>
        New assignment name
      </label>
      <InputText
        invalid={invalidName}
        value={assignmentName}
        onChange={e => {
          setInvalidName(false);
          setAssignmentName(e.target.value);
        }}
        placeholder="E.g.: Homework for next week"
        className="w-full"
        id="asssignment-name"
        disabled={previewEnabled}
      />
      {invalidName && (
        <p className="p-error py-0 my-0 font-semibold absolute">
          You need to assign a name to the assignment before proceeding.
        </p>
      )}
    </div>
  );
  const activateFocusedItemOnEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return;

    const focused = event.target as HTMLElement;
    const option = focused.closest<HTMLElement>('[role="option"]');
    const selectAll = focused
      .closest('.p-multiselect-select-all')
      ?.querySelector<HTMLElement>('input[type="checkbox"]');

    const clickable = option ?? selectAll;
    if (!clickable) return;

    event.preventDefault();
    clickable.click();
  };

  const classField = (
    <div className={`field ${showSiteDropdown ? 'col-6' : 'col-5 mx-1'}`} onKeyDown={activateFocusedItemOnEnter}>
      <label htmlFor="assignment-class">Class</label>
      <MultiSelect
        inputId="assignment-class"
        placeholder={showSiteDropdown && !selectedSite ? 'Select a site first' : 'Select classes'}
        value={selectedClasses}
        options={sortClassesList(classesForSite)}
        onChange={e => setSelectedClasses(e.value as Class[])}
        optionLabel="sectionName"
        dataKey="id"
        display="chip"
        filter
        filterPlaceholder="Search by..."
        className="w-full nc_assignment-class-select"
        disabled={previewEnabled || (showSiteDropdown && !selectedSite)}
      />
    </div>
  );
  if (!showSiteDropdown) {
    return (
      <div className="w-full">
        <div className="flex align-items-center col-12">
          {nameField}
          {classField}
        </div>
      </div>
    );
  }
  return (
    <div className="w-full">
      {nameField}
      <div className="flex align-items-center col-12 pt-0 px-0">
        <div className="field col-6">
          <label htmlFor="assignment-site">Site</label>
          <Dropdown
            inputId="assignment-site"
            placeholder="Select a site"
            value={selectedSite}
            options={sites}
            onChange={e => {
              setSelectedSite(e.value as Site);
              setSelectedClasses([]);
            }}
            optionLabel="name"
            dataKey="id"
            className="w-full"
            disabled={previewEnabled || sites.length === 1}
          />
        </div>
        {classField}
      </div>
    </div>
  );
};
